import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { useContactInfo } from '../../hooks/useContactInfo';

const FloatingWhatsApp: React.FC = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [isTooltipVisible, setIsTooltipVisible] = useState(false);
  const { contactInfo } = useContactInfo();

  useEffect(() => {
    // Show the button after a short delay
    const timer = setTimeout(() => {
      setIsVisible(true);
      // Show tooltip after button appears
      setTimeout(() => {
        setIsTooltipVisible(true);
        // Hide tooltip after 5 seconds
        setTimeout(() => {
          setIsTooltipVisible(false);
        }, 5000);
      }, 1000);
    }, 2000);

    return () => clearTimeout(timer);
  }, []);

  const handleWhatsAppClick = () => {
    const message = encodeURIComponent(
      'Hello! I\'m interested in learning more about NWLC courses and programs.'
    );
    
    // Use WhatsApp number from contact info or default
    let whatsappNumber = contactInfo.whatsapp || contactInfo.phone || '+9779841234567';
    
    // Clean the number: remove spaces, dashes, parentheses, and ensure proper format
    whatsappNumber = whatsappNumber
      .replace(/[\s\-\(\)]/g, '') // Remove spaces, dashes, parentheses
      .replace(/^\+/, ''); // Remove leading + if present
    
    // Ensure the number starts with country code (977 for Nepal if not present)
    if (!whatsappNumber.startsWith('977') && !whatsappNumber.startsWith('1')) {
      // If number doesn't start with a country code, add Nepal's code
      if (whatsappNumber.startsWith('98') || whatsappNumber.startsWith('97')) {
        whatsappNumber = '977' + whatsappNumber;
      }
    }
    
    // Construct the WhatsApp URL
    const whatsappUrl = `https://wa.me/${whatsappNumber}?text=${message}`;
    
    // Open WhatsApp in a new tab
    window.open(whatsappUrl, '_blank', 'noopener,noreferrer');
  };

  if (!isVisible) return null;

  return (
    <>
      {/* Floating WhatsApp Button */}
      <div className="fixed bottom-6 right-6 z-50">
        <div className="relative group">
          {/* Tooltip/Chat Bubble */}
          <div
            className={`absolute bottom-20 right-0 bg-white rounded-lg shadow-xl p-4 w-64 transition-all duration-300 ${
              isTooltipVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-95 pointer-events-none'
            }`}
            style={{
              animation: isTooltipVisible ? 'bounceFloat 2s ease-in-out infinite' : 'none'
            }}
          >
            <button
              onClick={() => setIsTooltipVisible(false)}
              className="absolute top-2 right-2 text-gray-400 hover:text-gray-600 transition-colors"
              aria-label="Close tooltip"
            >
              <X size={16} />
            </button>
            <p className="text-sm text-gray-700 pr-6 font-medium">
              👋 Need help? Chat with us on WhatsApp!
            </p>
            <p className="text-xs text-gray-500 mt-1">
              We typically reply within minutes
            </p>
            {/* Arrow pointing to button */}
            <div className="absolute bottom-0 right-6 transform translate-y-1/2 rotate-45 w-4 h-4 bg-white border-r border-b border-gray-200"></div>
          </div>

          {/* WhatsApp Button with proper logo - Smaller size */}
          <button
            onClick={handleWhatsAppClick}
            onMouseEnter={() => setIsTooltipVisible(true)}
            onMouseLeave={() => setIsTooltipVisible(false)}
            className="relative bg-green-500 hover:bg-green-600 text-white rounded-full p-3 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-110 group"
            aria-label="Chat on WhatsApp"
          >
            {/* Pulse animation ring */}
            <div className="absolute inset-0 rounded-full bg-green-500 animate-ping opacity-25"></div>
            
            {/* WhatsApp SVG Icon - Smaller */}
            <svg
              className="w-5 h-5 relative z-10"
              fill="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.149-.67.149-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.151-.172.2-.296.3-.495.099-.198.05-.372-.025-.521-.075-.148-.669-1.611-.916-2.206-.242-.579-.487-.501-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.076 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.695.248-1.29.173-1.414-.074-.123-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
            </svg>

            {/* Notification dot - Smaller */}
            <span className="absolute top-0 right-0 w-2.5 h-2.5 bg-red-500 rounded-full border-2 border-white"></span>
          </button>
        </div>
      </div>

      <style>{`
        @keyframes bounceFloat {
          0%, 100% {
            transform: translateY(0);
          }
          50% {
            transform: translateY(-10px);
          }
        }

        @media (max-width: 640px) {
          .fixed.bottom-6.right-6 {
            bottom: 20px;
            right: 20px;
          }
          
          /* Even smaller on mobile */
          button[aria-label="Chat on WhatsApp"] {
            padding: 10px;
          }
          
          button[aria-label="Chat on WhatsApp"] svg {
            width: 18px;
            height: 18px;
          }
        }

        /* Improve visibility on all backgrounds */
        button[aria-label="Chat on WhatsApp"] {
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
        }

        button[aria-label="Chat on WhatsApp"]:hover {
          box-shadow: 0 6px 20px rgba(0, 0, 0, 0.2);
        }
      `}</style>
    </>
  );
};

export default FloatingWhatsApp;